import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Italic,
  Bold,
  Pilcrow,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Underline as UnderlineIcon
} from 'lucide-react';

export default function Editor({
  onChange
}: {
  onChange: (content: string) => void;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          HTMLAttributes: {
            class: 'text-3xl'
          }
        },

        bulletList: {
          HTMLAttributes: {
            class: 'list-disc ml-3'
          }
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal ml-3'
          }
        }
      }),
      Underline,
      Link
    ],
    editorProps: {
      attributes: {
        class: 'min-h-[200px] border p-2 rounded-md'
      }
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    }
  });

  if (!editor) {
    return null;
  }

  return (
    <div className='space-y-4 rounded-lg border p-4'>
      {/* Toolbar */}
      <div className='flex flex-wrap items-center gap-2 border-b pb-2'>
        <ToggleGroup type='multiple'>
          <ToggleGroupItem
            value='heading1'
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            <Heading1 />
          </ToggleGroupItem>

          <ToggleGroupItem
            value='heading2'
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <Heading2 />
          </ToggleGroupItem>

          <ToggleGroupItem
            value='heading3'
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <Heading3 />
          </ToggleGroupItem>
          <ToggleGroupItem
            value='paragraph'
            onClick={() => editor.chain().focus().setParagraph().run()}
          >
            <Pilcrow />
          </ToggleGroupItem>
          <ToggleGroupItem
            value='bold'
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold />
          </ToggleGroupItem>
          <ToggleGroupItem
            value='italic'
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic />
          </ToggleGroupItem>
          <ToggleGroupItem
            value='underline'
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon />
          </ToggleGroupItem>
          <ToggleGroupItem
            value='bullet-list'
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List />
          </ToggleGroupItem>
          <ToggleGroupItem
            value='ordered-list'
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
}
